import { createSlice } from "@reduxjs/toolkit";
import api from "../utils/api";
import { baseURL } from "../settings.js/dev";
import { updateFlexTrack } from "./cart";
import { deepTransformKeys } from "../utils/object";
import { toSnakeCase } from "../utils/text";

const initialState = {
  isCbInitiated: false,
  cbInstance: "",
  googleMapKey: "",
  cbPublishableKey: "",
  cbRetentionAppId: null,
  hostedPageId: null,
  _3dsEnabled: false,
  paymentModule: [],
  isApplePaySupported: false,
  payPalCurrencies: [],
  // Per-session gateway A/B assignment, resolved server-side from the checkout email. null means
  // "not resolved yet" — distinct from false — so the payment page can wait rather than guess a
  // submit path and send the customer to the wrong gateway.
  sessionIs3dsEnabled: null,
  gatewayBucket: null
};

export const chargebee = createSlice({
  name: "chargebee",
  initialState,
  reducers: {
    loadChargebee: (state, action) => {
      let { site, googleMapKey, cbPublishableKey, cbRetentionAppId, flexTrackAdvertiserId, isApplePayEnabled, isGooglePayEnabled, cbInstance, paymentModule, isApplePaySupported, payPalCurrencies } = action.payload;
      state.cbInstance = cbInstance;
      window.flextrack.init(flexTrackAdvertiserId);
      state.googleMapKey = googleMapKey;
      state.cbPublishableKey = cbPublishableKey;
      state.cbRetentionAppId = cbRetentionAppId;
      state.isCbInitiated = true;
      state.paymentModule = paymentModule;
      state.isApplePaySupported = isApplePaySupported;
      state.payPalCurrencies = payPalCurrencies ?? [];
    },
    setHostedPageId: (state, action) => {
      state.hostedPageId = action.payload.id
    },
    setPaymentModuleSettings: (state, action) => {
      state.paymentModule = action.payload.paymentModule;
      state.isApplePaySupported = action.payload.isApplePaySupported;
    },
    setGatewayAssignment: (state, action) => {
      state.sessionIs3dsEnabled = action.payload.is3dsEnabled;
      state.gatewayBucket = action.payload.bucket;
    }
  },
});

export const createCheckoutForNewSubscription = (payload) => async (dispatch) => {
  const response = await api.post(baseURL, "/checkout/hosted-page", payload);
  dispatch(setHostedPageId(response));
  return response;
};

export const createCheckoutForUpdateSubscription = (payload) => async (dispatch) => {
  const response = await api.post(
    baseURL,
    "/checkout/update-hosted-page",
    payload
  );
  dispatch(setHostedPageId(response));
  return response;
};

export const createCheckoutForUpdatePaymentSource = () => async (dispatch) => {
  const response = await api.get(baseURL, '/billing/update-payment');
  return response;
}

export const retrieveHostedPage = (id) => async (dispatch) => {
  const response = await api.get(baseURL, `/checkout/get-hosted-page-content/${id}`);
  dispatch(updateFlexTrack({ flexTrack: response }));
  return response;
}

export const initChargebee = () => async (dispatch) => {
  let response = await api.post(baseURL, '/site');
  const cb_init_response = await initChargebeeInstance(response);
  response = { ...response, ...cb_init_response };
  dispatch(loadChargebee(response));
  return response;
};

// Resolves which gateway bucket this checkout belongs to, and therefore whether the card form
// submits via 3DS payment intents (Braintree) or tokenize (Smart Routing). Assignment is a pure
// function of the email server-side, so this agrees with the gateway the payment intent is actually
// stamped with, and stays the same across refreshes and retries.
// Sequence number for the assignment request in flight. The checkout page fires this as soon as the
// email is known and again when the currency arrives, so two requests can overlap — and the first
// one resolves to CONTROL, because an assignment with no currency matches no gateway account. If
// that stale response landed last it would silently drop a treatment-bucket customer out of the
// test while the logs still read bucket: braintree. Only the newest request is allowed to write.
//
// Module-scoped because only the payment page resolves assignments; two pages doing so concurrently
// would cancel each other.
let latestGatewayAssignmentRequest = 0;

// The payment page will not render until this resolves — it cannot show a card form before it knows
// which submit path that form takes. So a request that hangs doesn't degrade the checkout, it
// removes it. The timeout converts "page never appears" into "customer checks out on Worldpay",
// which is the same outcome as any other assignment failure.
//
// Generous on purpose: the endpoint only hashes an email and reads static config, so anything near
// this is a stuck connection rather than a slow one. Timing out early would quietly push customers
// on poor connections out of the test and skew the arms.
const GATEWAY_ASSIGNMENT_TIMEOUT_MS = 8000;

export const fetchGatewayAssignment = (email, currencyCode) => async (dispatch) => {
  const requestId = ++latestGatewayAssignmentRequest;
  const isStale = () => requestId !== latestGatewayAssignmentRequest;
  // Drop the previous answer before asking again. The usual reason for a second request is that the
  // currency arrived after the email, and the first answer was resolved without one — which lands in
  // control, because no gateway account matches an unknown currency. Leaving that on screen would
  // let the customer submit against an assignment we already know is wrong.
  //
  // This is why the page treats null as "not ready" rather than "no 3DS": callers must wait for a
  // resolved assignment, not fall through to the control path.
  dispatch(setGatewayAssignment({ is3dsEnabled: null, bucket: null }));
  try {
    // Currency decides which Braintree gateway account the treatment bucket lands on — the SCA
    // account for EUR/GBP, the default one otherwise — so it has to travel with the email.
    const response = await api.post(
      baseURL,
      '/checkout/gateway-assignment',
      { email, currencyCode },
      { timeout: GATEWAY_ASSIGNMENT_TIMEOUT_MS }
    );
    if (isStale()) {
      return null;
    }
    dispatch(setGatewayAssignment({
      is3dsEnabled: response?.is3dsEnabled === true,
      bucket: response?.bucket ?? null
    }));
    return response;
  } catch (error) {
    // Fail onto the control path rather than blocking checkout. Tokenize + Smart Routing is exactly
    // today's behaviour, so a customer whose assignment call fails still gets a working payment
    // form — they just aren't in the experiment.
    //
    // Staleness is checked here too: a newer request succeeding must not be overwritten by an older
    // one failing. The fallback still applies when it is the current request that failed.
    if (isStale()) {
      return null;
    }
    dispatch(setGatewayAssignment({ is3dsEnabled: false, bucket: null }));
    return null;
  }
};

export const createIntent = async (payload) => {
  const response = await api.post(baseURL, '/checkout/payment-intent', payload);
  return deepTransformKeys(response, toSnakeCase);
}

export const updateIntent = async (payload) => {
  const response = await api.post(baseURL, '/checkout/update-payment-intent', payload);
  return deepTransformKeys(response, toSnakeCase);
}

export const initChargebeeInstance = async (payload) => {
  let { site, cbPublishableKey, isApplePayEnabled, isGooglePayEnabled, isPayPalEnabled } = payload;
  let cbInstance = window.Chargebee.init({
    site: site,
    iframeOnly: true,
    publishableKey: cbPublishableKey,
    enableGTMTracking: true
  });

  let paymentModule = [];
  if (isApplePayEnabled)
    paymentModule = [...paymentModule, 'apple_pay'];
  if (isGooglePayEnabled)
    paymentModule = [...paymentModule, 'google_pay'];
  if (isPayPalEnabled)
    paymentModule = [...paymentModule, 'paypal_express_checkout'];

  let isApplePaySupported = false;

  if (cbInstance != null) {
    if (paymentModule.includes('apple_pay')) {
      let applePayHandler = await cbInstance.load("apple-pay");
      isApplePaySupported = applePayHandler.canMakePayments();

      if (!isApplePaySupported) {
        paymentModule = paymentModule.filter(pm => pm !== 'apple_pay');
      }
    }
  }

  return { cbInstance, paymentModule, isApplePaySupported };
}


export const { loadChargebee, setHostedPageId, setPaymentModuleSettings, setGatewayAssignment } = chargebee.actions;

export default chargebee.reducer;
